import {
  useModals,
  Modal,
  ModalProps,
  Avatar,
  Button,
  FontAwesomeIcon,
  Skeleton,
  Stack,
  Title,
} from "@skailer-com/uikit";
import { useTimeOffCategories, useTimeOffCategoriesSort } from "@/queries/use-time-off.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { ApiTimeOffCategory } from "@/types/time-off/time-off-policy.types.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export function TimeOffSortModal(props: ModalProps) {
  const { data: categories, isPending } = useTimeOffCategories({ enabled: true });
  const { t } = useTranslation();
  const [sortedCategories, setSortedCategories] = useState<ApiTimeOffCategory[]>(categories ?? []);
  const [syncedCategories, setSyncedCategories] = useState(categories);
  const timeOffCategoriesSort = useTimeOffCategoriesSort();
  const { id } = props;
  const { close } = useModals();

  if (categories !== syncedCategories) {
    setSyncedCategories(categories);
    if (categories) setSortedCategories(categories);
  }

  const saveOrder = () => {
    timeOffCategoriesSort.mutate(
      sortedCategories.map((category) => category.id),
      {
        onSuccess: () => {
          analyticsService.trackEvent(analyticEvents.timeOffs.categoriesSorted);
          close(id);
        },
      }
    );
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) return;

    const items = Array.from(sortedCategories);
    const [moved] = items.splice(source.index, 1);

    items.splice(destination.index, 0, moved);

    setSortedCategories(items);
  };

  return (
    <Modal
      title={t("common:reordering")}
      {...props}
      withCloser={true}
      actions={
        <Stack>
          <Button size={"lg"} onClick={saveOrder} isLoading={timeOffCategoriesSort.isPending}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      {isPending ? (
        <Stack direction={"vertical"} gap={"md"}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Stack key={index} direction={"horizontal"} gap={"md"} items={"center"}>
              <Avatar size={"xs"} />
              <Skeleton className={"h-4 w-48"} />
            </Stack>
          ))}
        </Stack>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={"categories-list"} direction={"vertical"}>
            {(provided) => (
              <div
                className={"w-full flex flex-col overflow-y-auto"}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {sortedCategories.map((category, index) => (
                  <Draggable key={category.id} draggableId={category.id.toString()} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                        }}
                        className={"flex items-center gap-4 bg-light py-2 min-h-16"}
                      >
                        <div
                          className={"flex items-center justify-center cursor-grab text-secondary"}
                        >
                          <FontAwesomeIcon icon="fa-light fa-grip-dots-vertical" />
                        </div>
                        <Avatar size={"xl"} emoji={category.emoji} />
                        <Title header={category.title} />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Modal>
  );
}
